import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMenu } from '../contexts/MenuContext';
import MenuCategory from '../components/MenuCategory';
import Cart from '../components/Cart';
import { ShoppingCart, ChevronDown } from 'lucide-react';

const MenuPage: React.FC = () => {
  const { categories, menuItems, loading, getCartItemCount } = useMenu();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [openCategoryIds, setOpenCategoryIds] = useState<Set<string>>(new Set());
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const listTopRef = useRef<HTMLDivElement | null>(null);
  
  // Check if a specific item is being viewed (from URL)
  const itemIdFromUrl = searchParams.get('item');
  
  // Check if cart should be shown (from URL)
  const showCart = searchParams.get('cart') === 'true';

  // Debug logging
  console.log('MenuPage - Loading:', loading);
  console.log('MenuPage - Categories:', categories.length);
  console.log('MenuPage - Menu Items:', menuItems.length);

  // Filter out inactive categories and unavailable items
  const activeCategories = categories.filter(category => category.isActive);
  const orderedActiveCategories = activeCategories
    .slice()
    .sort((a, b) => (typeof a.order === 'number' ? a.order : Number(a.order ?? 1e9)) - (typeof b.order === 'number' ? b.order : Number(b.order ?? 1e9)));
  const availableItems = menuItems.filter(item => item.isAvailable);
  
  // Toggle category expansion - only one can be expanded at a time
  const toggleCategory = (categoryId: string) => {
    const headerEl = categoryRefs.current[categoryId];
    setOpenCategoryIds(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
    if (headerEl) {
      headerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Get items for a specific category
  const getCategoryItems = (categoryId: string) => {
    return availableItems
      .filter(item => item.categoryId === categoryId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="relative w-12 h-12 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-red-200 border-t-red-500 rounded-full animate-spin"></div>
            <div className="absolute inset-1 bg-black rounded-full flex items-center justify-center overflow-hidden">
              <img src="/logo1.png" alt="Logo" className="h-8 w-8 object-contain" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Loading Menu</h3>
          <p className="text-gray-600 mb-4">Preparing our delicious offerings...</p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              <strong>Taking too long?</strong> Check your browser console for errors or verify Firebase configuration.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <Helmet>
        <title>Menu - Kiss Coffee Minehead</title>
        <link rel="canonical" href="https://kisscoffeeminehead.co.uk/menu" />
        <meta name="robots" content="index,follow" />
      </Helmet>
      {/* Header */}
      <div className="relative text-white shadow-2xl pt-4 overflow-hidden">
        {/* Background image */}
        <img 
          src="/banner-background.jpg" 
          alt="Menu background"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'blur(1px)',
            zIndex: 1
          }}
        />
        
        {/* Dark overlay */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.75)',
            zIndex: 2
          }}
        ></div>
        
        <div className="container mx-auto px-4 py-6 md:py-8 relative z-10">
          <div className="space-y-4 text-center">
            {/* Top row: Title only */}
            <div>
              <h1 className="font-bold"><span className="text-gray-200 text-base sm:text-lg md:text-xl" style={{ textShadow: '0 4px 6px rgba(0, 0, 0, 0.9), 0 2px 4px rgba(0, 0, 0, 0.9)' }}>Our </span><span className="text-red-500 text-2xl sm:text-3xl md:text-4xl" style={{ textShadow: '0 4px 6px rgba(0, 0, 0, 0.9), 0 2px 4px rgba(0, 0, 0, 0.9)' }}>Menu</span></h1>
            </div>
            
            {/* Bottom row: Description */}
            <p className="text-gray-300 text-xs sm:text-sm" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>Discover our delicious coffee and food offerings</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4 sm:py-6 md:py-8">
        {/* If viewing a specific item from URL, only render that item */}
        {itemIdFromUrl ? (
          <div>
            {menuItems
              .filter(item => item.id === itemIdFromUrl)
              .map((item) => (
                <MenuCategory key={item.id} item={item} />
              ))}
          </div>
        ) : (
          <>
        {/* Cart Button - Only show when cart has items */}
        {getCartItemCount() > 0 && (
          <div className="fixed top-20 sm:top-20 right-4 z-40">
            <button
              onClick={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.set('cart', 'true');
                setSearchParams(newParams);
              }}
              className="group bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-xl inline-flex items-center relative text-sm shadow-lg"
            >
              <ShoppingCart className="h-4 w-4 mr-2 group-hover:rotate-12 transition-transform" />
              <span className="hidden sm:inline">View Cart</span>
              <span className="sm:hidden">Cart</span>
              <span className="absolute -top-2 -right-2 bg-white text-red-600 text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-md border border-red-600">
                {getCartItemCount()}
              </span>
            </button>
          </div>
        )}

            {/* Accordion Menu Layout */}
            <div className="max-w-6xl mx-auto">
              <div ref={listTopRef} />
              {orderedActiveCategories.length === 0 ? (
              <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No menu categories available</p>
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg max-w-md mx-auto">
                    <p className="text-yellow-800 text-sm">
                      <strong>No menu data found.</strong> Please set up Firebase and add sample data using the setup guide.
                    </p>
                    <a 
                      href="SETUP_GUIDE.md" 
                      target="_blank" 
                      className="text-red-600 hover:text-red-800 text-sm underline mt-2 inline-block"
                    >
                      View Setup Guide →
                    </a>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {orderedActiveCategories.map((category) => {
                    const categoryItems = getCategoryItems(category.id);
                    const anyOpen = openCategoryIds.size > 0;
                    const isExpanded = openCategoryIds.has(category.id);

                    return (
                      <div 
                        key={category.id}
                        className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 scroll-mt-24"
                        ref={(el) => { categoryRefs.current[category.id] = el; }}
                      >
                        {/* Category Header - Clickable */}
                        <button
                          onClick={() => toggleCategory(category.id)}
                        className={`w-full flex items-center justify-between transition-all duration-200 hover:bg-gray-50 ${
                            anyOpen 
                              ? 'px-3 sm:px-4 py-2 sm:py-3' 
                              : 'px-4 sm:px-6 py-4 sm:py-5'
                          }`}
                        >
                          <div className={`flex items-center ${anyOpen ? 'space-x-2 sm:space-x-3' : 'space-x-3 sm:space-x-4'}`}>
                            <div className="bg-black rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center border border-red-500">
                              <img src="/logo1.png" alt="Logo" className="h-3 w-3 sm:h-4 sm:w-4 object-contain" />
                            </div>
                            <div className="text-left">
                              <h2 className={`font-bold text-gray-800 transition-all duration-200 ${
                                isExpanded
                                  ? 'text-lg sm:text-xl md:text-2xl'
                                  : anyOpen
                                  ? 'text-sm sm:text-base'
                                  : 'text-lg sm:text-xl md:text-2xl'
                              }`}>
                                {category.name}
                              </h2>
                              {category.description && !anyOpen && (
                                <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                                  {category.description}
                                </p>
                )}
              </div>
                          </div>
                          <div className="flex items-center space-x-2 sm:space-x-3">
                            <span className="text-xs sm:text-sm text-gray-500 font-medium">
                              {categoryItems.length} {categoryItems.length === 1 ? 'item' : 'items'}
                            </span>
                            <ChevronDown 
                              className={`text-red-500 transition-all duration-300 ${
                                isExpanded 
                                  ? 'h-4 w-4 sm:h-5 sm:w-5 rotate-180' 
                                  : 'h-5 w-5 sm:h-6 sm:w-6'
                              }`}
                            />
                          </div>
                        </button>

                        {/* Category Items - Collapsible */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: 'easeInOut' }}
                              className="overflow-hidden"
                            >
                              <div className="border-t border-gray-200 p-4 sm:p-6 bg-gray-50">
                                {categoryItems.length === 0 ? (
                                  <p className="text-center text-gray-500 py-8">
                                    No items available in this category
                                  </p>
                                ) : (
                                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
                                    {categoryItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                                        initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                  >
                    <MenuCategory item={item} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              )}
        </div>
          </>
        )}
      </div>

      {/* Cart Modal */}
      {showCart && (
        <Cart onClose={() => navigate('/menu')} />
      )}
    </div>
  );
};

export default MenuPage;
